import React, { memo, useMemo } from 'react';

import { Button, Typography } from 'antd';
import HtmlParser from 'html-react-parser';
import moment from 'moment';
import TMIcon from 'tm-icons-library';

import { useTheme } from '../../../../context/ThemeProvider';
import {
  AssignmentQuestionCategory,
  IObtainedMarksForQuestion,
} from '../../../../types/assignmentTypes';
import { useAssignmentContext } from '../../utils/AssignmentProvider';
import AssignmentAttachments from '../AssignmentAttachments/AssignmentAttachments';
import AssignmentQuestionOptions from '../AssignmentQuestionOptions/AssignmentQuestionOptions';
import AssignmentSubjectiveQuestion from '../AssignmentSubjectiveQuestion/AssignmentSubjectiveQuestion';

interface Props {
  question?: IObtainedMarksForQuestion;
}

const AssignmentQuestion: React.FC<Props> = ({ question }) => {
  const {
    _id: questionId = '',
    images,
    question: questionTitle,
    type,
    marks,
    selectedOptions,
    subjectiveAnswer,
    wordLimit,
    allowFileUploads,
    attachments,
    maxFileCount,
    maxFileSize,
    allowedFileTypes,
  } = question || {};

  const { saveQuestionAnswer, flow, currentIndex, submission, assignment } =
    useAssignmentContext();

  const { obtainedMarksForQuestions = [] } = submission || {};

  // const [currentImage, setCurrentImage] = React.useState<string | null>(null);

  const questionImages = useMemo(() => {
    return images || [];
  }, [images]);

  const hasQuestionAnswer = useMemo(() => {
    return (
      (type !== AssignmentQuestionCategory.SUBJECTIVE &&
        selectedOptions?.length) ||
      (type === AssignmentQuestionCategory.SUBJECTIVE &&
        subjectiveAnswer?.trim().length)
    );
  }, [type, selectedOptions, subjectiveAnswer]);

  const isCorrectAnswer = useMemo(() => {
    if (type === AssignmentQuestionCategory.MULTIPLE_SELECT) {
      return (
        question?.selectedOptions?.length ===
          question?.correctOptions?.length &&
        question?.selectedOptions?.every((option) =>
          question?.correctOptions?.includes(option),
        )
      );
    }

    // if question is single select, check if the selected option is correct
    if (type === AssignmentQuestionCategory.SINGLE_SELECT) {
      return question?.selectedOptions?.[0] === question?.correctOptions?.[0];
    }

    return false;
  }, [question, type]);

  const { colors: COLORS } = useTheme();

  return (
    <div>
      {flow === 'review' ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12,
          }}>
          <div>
            {`Question ${currentIndex + 1}/${obtainedMarksForQuestions.length}`}
          </div>

          <Button
            style={{
              borderWidth: 1,
              borderColor: '#d9d9d9',
              borderRadius: 12,
            }}>
            Marks: {marks || 0}
          </Button>
        </div>
      ) : null}

      <div style={{ flex: 1 }}>
        {questionTitle ? HtmlParser(questionTitle) : null}
      </div>

      {questionImages.length > 0 ? (
        <div
          // onClick={() => setCurrentImage(questionImages[0])}
          style={{
            marginTop: 12,
            borderRadius: 12,
            border: '1px solid #d9d9d9',
            cursor: 'pointer',
            overflow: 'hidden',
          }}>
          <img
            src={questionImages[0]}
            style={{
              width: '100%',
              aspectRatio: '16 / 9',
              borderRadius: 12,
              objectFit: 'cover',
            }}
            alt="Question image"
          />
        </div>
      ) : null}

      <div>
        {type !== AssignmentQuestionCategory.SUBJECTIVE ? (
          <AssignmentQuestionOptions
            key={questionId}
            questionId={questionId}
            question={question}
            selectedOptions={selectedOptions || []}
          />
        ) : (
          <AssignmentSubjectiveQuestion
            key={questionId}
            questionId={questionId}
            subjectiveAnswer={subjectiveAnswer || ''}
            characterLimit={wordLimit && wordLimit > 0 ? wordLimit * 5 : 0}
            attachments={attachments || []}
          />
        )}

        {hasQuestionAnswer &&
        type !== AssignmentQuestionCategory.SUBJECTIVE &&
        flow === 'submit' ? (
          <div
            onClick={() =>
              saveQuestionAnswer(questionId, 'clear', {
                selectedOptions: [],
              })
            }
            style={{
              marginTop: 8,
              textDecoration: 'underline',
              textAlign: 'right',
              color: '#666',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '400',
            }}>
            Clear answer
          </div>
        ) : null}

        {allowFileUploads ? (
          <AssignmentAttachments
            questionId={questionId}
            attachments={attachments || []}
            maxFileCount={maxFileCount || 0}
            maxFileSize={maxFileSize || 0} // in bytes
            allowedFileTypes={allowedFileTypes || []}
            subjectiveAnswer={subjectiveAnswer || ''}
          />
        ) : null}
      </div>

      {question && flow === 'review' ? (
        <div
          style={{
            marginTop: 12,
            borderTop: '1px solid',
            borderColor: COLORS.BORDER,
            paddingTop: 24,
          }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 8,
              border: '1px solid',
              borderColor:
                (question?.selectedOptions?.length ?? 0) > 0 ||
                (question?.subjectiveAnswer?.trim().length ?? 0) > 0 ||
                (question?.attachments?.length ?? 0) > 0
                  ? question.type ===
                      AssignmentQuestionCategory.MULTIPLE_SELECT ||
                    question.type === AssignmentQuestionCategory.SINGLE_SELECT
                    ? isCorrectAnswer
                      ? COLORS.GREEN
                      : COLORS.DANGER
                    : COLORS.BLUE
                  : COLORS.TEXT1,
              backgroundColor:
                (question?.selectedOptions?.length ?? 0) > 0 ||
                (question?.subjectiveAnswer?.trim().length ?? 0) > 0 ||
                (question?.attachments?.length ?? 0) > 0
                  ? question.type ===
                      AssignmentQuestionCategory.MULTIPLE_SELECT ||
                    question.type === AssignmentQuestionCategory.SINGLE_SELECT
                    ? isCorrectAnswer
                      ? `${COLORS.GREEN}20`
                      : `${COLORS.DANGER}20`
                    : `${COLORS.BLUE}20`
                  : COLORS.BACKGROUND,
              padding: 12,
              borderRadius: 12,
            }}>
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              {(question?.selectedOptions?.length ?? 0) > 0 ||
              (question?.subjectiveAnswer?.trim().length ?? 0) > 0 ||
              (question?.attachments?.length ?? 0) > 0 ? (
                question.type === AssignmentQuestionCategory.MULTIPLE_SELECT ||
                question.type === AssignmentQuestionCategory.SINGLE_SELECT ? (
                  isCorrectAnswer ? (
                    <TMIcon name="checkmark" size={20} color={COLORS.GREEN} />
                  ) : (
                    <TMIcon name="close" size={20} color={COLORS.DANGER} />
                  )
                ) : (
                  <TMIcon name="document-text" size={20} color={COLORS.BLUE} />
                )
              ) : (
                <TMIcon name="remove" size={20} color={COLORS.TEXT1} />
              )}
            </div>
            <div>
              <Typography.Text
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color:
                    (question?.selectedOptions?.length ?? 0) > 0 ||
                    (question?.subjectiveAnswer?.trim().length ?? 0) > 0 ||
                    (question?.attachments?.length ?? 0) > 0
                      ? question.type ===
                          AssignmentQuestionCategory.MULTIPLE_SELECT ||
                        question.type ===
                          AssignmentQuestionCategory.SINGLE_SELECT
                        ? isCorrectAnswer
                          ? COLORS.GREEN
                          : COLORS.DANGER
                        : COLORS.BLUE
                      : COLORS.TEXT1,
                }}>
                {(question?.selectedOptions?.length ?? 0) > 0 ||
                (question?.subjectiveAnswer?.trim().length ?? 0) > 0 ||
                (question?.attachments?.length ?? 0) > 0
                  ? question.type ===
                      AssignmentQuestionCategory.MULTIPLE_SELECT ||
                    question.type === AssignmentQuestionCategory.SINGLE_SELECT
                    ? isCorrectAnswer
                      ? 'Correct answer'
                      : 'Wrong answer'
                    : 'Attempted'
                  : 'Not attempted'}
              </Typography.Text>
              <div style={{ marginTop: 4 }}>
                <Typography.Text style={{ fontSize: 14, color: COLORS.TEXT1 }}>
                  Marks achieved: {question?.obtainedMarks || 0}
                </Typography.Text>
              </div>
            </div>
          </div>

          {question?.remarks && question?.remarks?.length > 0 ? (
            <div style={{ marginTop: 12 }}>
              <Typography.Text style={{ fontSize: 14, color: COLORS.TEXT1 }}>
                Feedback:
              </Typography.Text>

              <div
                style={{
                  border: '1px solid',
                  borderColor: COLORS.BORDER,
                  borderRadius: 12,
                  padding: 12,
                  marginTop: 8,
                }}>
                {HtmlParser(question?.remarks?.[0] || '')}
              </div>
            </div>
          ) : null}

          {assignment?.reviewedAt && (
            <div style={{ marginTop: 12 }}>
              <Typography.Text style={{ fontSize: 14, color: COLORS.TEXT1 }}>
                Assignment Reviewed on{' '}
                {moment(assignment?.reviewedAt).format('MMM D, YYYY h:mm A')}
              </Typography.Text>
            </div>
          )}
        </div>
      ) : null}

      {/* TODO(rohan): Add image viewer */}
      {/* {currentImage ? (
        <CustomImageViewerComponent
          visible={!!currentImage}
          onRequestClose={() => setCurrentImage(null)}
          uri={currentImage}
        />
      ) : null} */}
    </div>
  );
};

export default memo(AssignmentQuestion);
